export const ACCOUNT_VALIDATION_LIMITS = {
  passwordMinLength: 8,
  passwordMaxBytes: 72,
  usernameMinLength: 3,
  usernameMaxLength: 32,
  displayNameMaxLength: 40,
  bioMaxLength: 120,
} as const;

const USERNAME_PATTERN = /^[a-z0-9_]+$/;
const EMAIL_PATTERN = /^[^\s@.](?:[^\s@]*[^\s@.])?@[^\s@.]+(?:\.[^\s@.]+)+$/;

export const USERNAME_HELP_COPY = `This will be your @username. Choose ${ACCOUNT_VALIDATION_LIMITS.usernameMinLength}-${ACCOUNT_VALIDATION_LIMITS.usernameMaxLength} characters: lowercase letters, numbers, or underscores.`;
export const USERNAME_ALLOWED_CHARACTERS_ERROR =
  'Use only lowercase letters, numbers, or underscores. No spaces or punctuation.';

export function normalizeUsernameInput(value: string) {
  return value.toLowerCase().slice(0, ACCOUNT_VALIDATION_LIMITS.usernameMaxLength);
}

export function validatePassword(password: string) {
  if (password.length < ACCOUNT_VALIDATION_LIMITS.passwordMinLength) {
    return `Use at least ${ACCOUNT_VALIDATION_LIMITS.passwordMinLength} characters for your password.`;
  }

  if (new TextEncoder().encode(password).length > ACCOUNT_VALIDATION_LIMITS.passwordMaxBytes) {
    return 'That password is too long. Use a shorter password.';
  }

  return null;
}

export function validateEmail(email: string) {
  const normalizedEmail = email.trim();
  return EMAIL_PATTERN.test(normalizedEmail) && !normalizedEmail.includes('..')
    ? null
    : 'Enter a valid email address.';
}

export function validatePasswordConfirmation(password: string, confirmPassword: string) {
  const passwordError = validatePassword(password);
  if (passwordError) {
    return passwordError;
  }

  if (password !== confirmPassword) {
    return 'Passwords do not match.';
  }

  return null;
}

export function validateUsername(username: string) {
  const trimmedUsername = username.trim().toLowerCase();

  if (!trimmedUsername) {
    return 'Username is required.';
  }

  if (
    trimmedUsername.length < ACCOUNT_VALIDATION_LIMITS.usernameMinLength ||
    trimmedUsername.length > ACCOUNT_VALIDATION_LIMITS.usernameMaxLength
  ) {
    return `Username must be ${ACCOUNT_VALIDATION_LIMITS.usernameMinLength}-${ACCOUNT_VALIDATION_LIMITS.usernameMaxLength} characters.`;
  }

  if (!USERNAME_PATTERN.test(trimmedUsername)) {
    return USERNAME_ALLOWED_CHARACTERS_ERROR;
  }

  return null;
}

export function validateDisplayName(displayName: string) {
  const trimmedDisplayName = displayName.trim();

  if (!trimmedDisplayName) {
    return 'Display name is required.';
  }

  if (trimmedDisplayName.length > ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength) {
    return `Display name must be ${ACCOUNT_VALIDATION_LIMITS.displayNameMaxLength} characters or fewer.`;
  }

  return null;
}

export function validateBio(bio: string) {
  if (bio.trim().length > ACCOUNT_VALIDATION_LIMITS.bioMaxLength) {
    return `Bio must be ${ACCOUNT_VALIDATION_LIMITS.bioMaxLength} characters or fewer.`;
  }

  return null;
}

export function validateProfileIdentity({
  bio = '',
  displayName,
  username,
}: {
  bio?: string;
  displayName: string;
  username: string;
}) {
  return validateUsername(username) ?? validateDisplayName(displayName) ?? validateBio(bio);
}
